import React, { useEffect, useId, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import { detectMicrophonePlatform } from './microphonePlatform';

const platforms = {
  'android-chrome': {
    label: 'Android · Chrome',
    steps: [
      'Chrome’u aç.',
      'Sağ üstteki ⋮ menüsüne dokun.',
      'Ayarlar → Site ayarları → Mikrofon bölümüne gir.',
      'Engellendi bölümünde DANDU sitesini bul.',
      'DANDU’ya dokun ve İzin ver seçeneğini seç.',
      'DANDU’ya dön ve aşağıdaki Tekrar Dene düğmesine dokun.',
    ],
    siteNote: 'DANDU listede yoksa sitelerin mikrofon kullanmak için izin isteyebildiğini kontrol et. Kısa yol: Adres çubuğundaki site bilgileri menüsünde İzinler → Mikrofon görünüyorsa buradan da izin verebilirsin.',
    device: 'Android Ayarlar → Uygulamalar → Chrome → İzinler → Mikrofon yolundan erişime izin ver. Menü adları Android sürümüne göre değişebilir.',
  },
  'ios-safari': {
    label: 'iPhone / iPad · Safari',
    steps: [
      'DANDU sitesini Safari’de aç.',
      'Adres çubuğunun solundaki Sayfa Menüsü simgesine dokun.',
      'Web Sitesi Ayarları’nı aç. Safari sürümüne göre önce ek menüyü (⋯) açman gerekebilir.',
      'Mikrofon seçeneğini bul ve İzin Ver’i seç.',
      'DANDU’ya dön ve aşağıdaki Tekrar Dene düğmesine dokun.',
    ],
    siteNote: 'Site ayarını bulamazsan aşağıdaki Safari ayarını da kontrol et. Genel Safari ayarı, DANDU’ya özel engeli kaldırmayabilir; site ayarına yeniden bak.',
    device: 'iPhone Ayarlar → Uygulamalar → Safari → Mikrofon bölümünde Sor veya İzin Ver seçeneğini kontrol et. Eski iOS sürümlerinde Safari doğrudan Ayarlar’da bulunabilir.',
  },
  'ios-chrome': {
    label: 'iPhone / iPad · Chrome',
    steps: [
      'DANDU sitesini Chrome’da aç.',
      'Adres çubuğunun solunda mikrofon simgesi varsa ona dokun.',
      'İzinler bölümünden DANDU’nun mikrofon erişimini aç.',
      'DANDU’ya dön ve aşağıdaki Tekrar Dene düğmesine dokun.',
    ],
    siteNote: 'Site izinlerini daha önce değiştirmediysen mikrofon simgesi görünmeyebilir. Simge yoksa veya izin değişmiyorsa aşağıdaki cihaz ayarını kontrol et; sonra DANDU’ya dönüp Tekrar Dene’ye dokun.',
    device: 'iPhone Ayarlar → Uygulamalar → Chrome → Mikrofon yolundan erişimi aç. Eski iOS sürümlerinde Chrome doğrudan Ayarlar’da bulunabilir. Sonra DANDU’ya dön ve Tekrar Dene’ye dokun; izin sorusu çıkarsa İzin Ver’i seç.',
  },
  other: {
    label: 'Diğer / Emin değilim',
    steps: [
      'Tarayıcında DANDU sitesini aç.',
      'Adres çubuğundaki site bilgileri veya tarayıcı ayarlarından DANDU sitesine özel mikrofon iznini bul ve erişime izin ver.',
      'DANDU’ya dön ve aşağıdaki Tekrar Dene düğmesine dokun.',
    ],
    siteNote: 'Menüler tarayıcıya göre değişir. Kullandığın tarayıcıyı biliyorsan yukarıdan seçerek ona uygun adımları görebilirsin.',
    device: 'Cihaz ayarlarında kullandığın tarayıcının mikrofon erişimini kontrol et ve erişime izin ver. Ardından DANDU’ya dönüp Tekrar Dene’ye dokun.',
  },
};

const focusStyle = { outline: '3px solid #5128bc', outlineOffset: 3 };
const detailsStyle = {
  '& > summary': { minHeight: 44, display: 'list-item', alignContent: 'center', cursor: 'pointer', fontWeight: 800, py: 1 },
  '& > summary:focus-visible': focusStyle,
};

export default function MicrophonePermissionHelp({ onRetry, busy = false, failures = 1 }) {
  const [platform, setPlatform] = useState(detectMicrophonePlatform);
  const [platformSelectorOpen, setPlatformSelectorOpen] = useState(platform === 'other');
  const id = useId();
  const titleRef = useRef(null);
  const guide = platforms[platform];
  useEffect(() => { titleRef.current?.focus(); }, []);

  return (
    <Alert severity="warning" icon={false} role="region" aria-labelledby={`${id}-title`}
      sx={{ mb: 2, bgcolor: '#fff8e6', color: '#784b16', borderRadius: 3, p: { xs: 1.5, sm: 2 }, '& .MuiAlert-message': { p: 0, width: '100%', minWidth: 0, overflow: 'visible' }, lineHeight: 1.65 }}>
      <Typography component="h2" id={`${id}-title`} ref={titleRef} tabIndex={-1}
        sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.4, '&:focus-visible': focusStyle }}>
        <span aria-hidden="true">🎙️ </span>DANDU için mikrofon iznini açalım
      </Typography>
      <Typography sx={{ mt: 1, lineHeight: 'inherit' }}>
        Telefonunda mikrofon açık olsa bile DANDU sitesi için engellenmiş olabilir. Birlikte kontrol edelim! 💜
      </Typography>
      <Box component="details" open={platformSelectorOpen} onToggle={event => setPlatformSelectorOpen(event.currentTarget.open)} sx={{ ...detailsStyle, mt: 1 }}>
        <summary>Cihaz / tarayıcı: {guide.label} · Değiştir</summary>
        <Box component="label" htmlFor={`${id}-platform`} sx={{ display: 'block', mt: 1, fontWeight: 700 }}>Kullandığın tarayıcı</Box>
        <Box component="select" id={`${id}-platform`} value={platform} onChange={event => setPlatform(event.target.value)}
          sx={{ boxSizing: 'border-box', width: '100%', maxWidth: '100%', minWidth: 0, minHeight: 48, px: 1, my: 1, borderRadius: 1, border: '1px solid #784b16', bgcolor: '#fff', color: '#172554', font: 'inherit', '&:focus-visible': focusStyle }}>
          {Object.entries(platforms).map(([value, item]) => <option key={value} value={value}>{item.label}</option>)}
        </Box>
      </Box>
      <Typography component="h3" sx={{ mt: 1, fontSize: 17, fontWeight: 900 }}>1. Önce DANDU site iznini kontrol et</Typography>
      <Box component="ol" sx={{ pl: 2.8, my: 1, '& li': { pl: 0.3, mb: 0.8 } }}>
        {guide.steps.map(step => <li key={step}>{step}</li>)}
      </Box>
      <Typography sx={{ fontSize: 14, lineHeight: 'inherit' }}>{guide.siteNote}</Typography>
      <Box component="details" sx={{ ...detailsStyle, mt: 1 }}>
        <summary>2. Olmazsa cihazdaki mikrofon erişimini kontrol et</summary>
        <Typography sx={{ lineHeight: 'inherit', mt: 0.5 }}>{guide.device}</Typography>
      </Box>
      <Typography sx={{ mt: 1, fontSize: 14, lineHeight: 'inherit' }}>İzin penceresi yeniden açılmayabilir. Ayarı değiştirdikten sonra tekrar deneyebilirsin.</Typography>
      <Box role="status" aria-live="polite" sx={{ mt: 1 }}>
        {busy ? 'Mikrofon kontrol ediliyor…' : failures > 1 ? 'Henüz erişemedik. Site ve cihaz ayarlarını yeniden kontrol edebilirsin. Ayar değişmiyorsa DANDU’yu doğrudan güncel Chrome veya Safari’de açmayı ya da bir yetişkinden yardım almayı deneyebilirsin.' : null}
      </Box>
      <Button variant="contained" disableRipple onClick={onRetry} disabled={busy} startIcon={<MicRoundedIcon />}
        sx={{ mt: 1, minHeight: 48, width: { xs: '100%', sm: 'auto' }, px: 3, borderRadius: 2.5, fontWeight: 900, textTransform: 'none', bgcolor: '#5128bc', color: '#fff', '&:hover': { bgcolor: '#43219d' }, '&.Mui-focusVisible': focusStyle }}>
        Tekrar Dene
      </Button>
    </Alert>
  );
}
